import * as React from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardActions,
    CardContent,
    Chip,
    CircularProgress,
    Divider,
    FormControlLabel,
    Grid,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
    createPresentationCampaign,
    deletePresentationCampaign,
    getFunnelOptions,
    getPresentationCampaigns,
    getPresentationScreenRegistry,
    getUserTags,
    updatePresentationCampaign,
} from '../Api';
import UserTagDefinitionDto from '../models/dtos/UserTagDefinitionDto';
import WebGameTemplateHtmlEditorDialog from '../components/WebGameTemplateHtmlEditorDialog';
import HtmlTemplatePreview from '../components/HtmlTemplatePreview';
import type { PresentationCampaignDto, PresentationCampaignUpsertRequest } from '../models/dtos/PresentationCampaignDto';
import type { PresentationScreenRegistryDto } from '../models/dtos/PresentationScreenRegistryDto';
import { BuildNumberAutocomplete, useAppVersionsForOsTypes } from '../utils/appVersionFields';

type PageMessage = { type: 'success' | 'error'; text: string } | null;

type FormErrors = {
    slug?: string;
    name?: string;
    screenKeys?: string;
    htmlContent?: string;
    maxViewsPerUser?: string;
    remindLaterMinutes?: string;
    tourCompletion?: string;
};

const TOUR_COMPLETION_FILTER_OPTIONS = [
    { value: 'completed', label: 'Finished tour (all steps)' },
    { value: 'skipped', label: 'Skipped tour' },
    { value: 'not_completed', label: 'In progress (started, not finished or skipped)' },
    { value: 'not_seen', label: 'Never started tour' },
] as const;

const parseTourCompletionFormValue = (value: string): string[] => {
    const normalized = (value || 'any').trim().toLowerCase();
    if (!normalized || normalized === 'any') {
        return [];
    }
    return normalized.split(',').map(part => part.trim()).filter(Boolean);
};

const serializeTourCompletionFormValue = (filters: string[]): string => {
    if (filters.length === 0) {
        return 'any';
    }
    return Array.from(new Set(filters.map(filter => filter.trim().toLowerCase()))).sort().join(',');
};

const validateTourCompletionFormValue = (value: string): string | undefined => {
    const parts = parseTourCompletionFormValue(value);
    if (parts.length === 0) {
        return undefined;
    }
    const allowed = TOUR_COMPLETION_FILTER_OPTIONS.map(option => option.value);
    if (parts.some(part => !allowed.includes(part as typeof allowed[number]))) {
        return 'Invalid tour filter. Use: completed, skipped, not_completed, not_seen.';
    }
    if (parts.includes('not_seen') && parts.length > 1) {
        return '“Never started” cannot be combined with other tour filters.';
    }
    if (parts.includes('not_completed') && (parts.includes('completed') || parts.includes('skipped'))) {
        return '“In progress” cannot be combined with completed or skipped.';
    }
    return undefined;
};

type PresentationScreenOption = {
    id: string;
    label: string;
    kind: 'group' | 'screen';
    description?: string;
};

function buildPresentationScreenOptions(registry: PresentationScreenRegistryDto | null): PresentationScreenOption[] {
    if (registry == null) {
        return [];
    }
    const options: PresentationScreenOption[] = [];
    const seen = new Set<string>();

    Object.entries(registry.groups ?? {}).forEach(([groupKey, members]) => {
        const id = groupKey.trim().toLowerCase();
        if (id.length === 0 || seen.has(id)) {
            return;
        }
        seen.add(id);
        options.push({
            id,
            label: id,
            kind: 'group',
            description: members.length > 0 ? `Matches any of: ${members.join(', ')}` : 'Screen group',
        });
    });

    (registry.screens ?? []).forEach(screen => {
        const id = screen.screenKey.trim().toLowerCase();
        if (id.length === 0 || seen.has(id)) {
            return;
        }
        seen.add(id);
        options.push({
            id,
            label: id,
            kind: 'screen',
            description: screen.className,
        });
    });

    return options.sort((left, right) => {
        if (left.kind !== right.kind) {
            return left.kind === 'group' ? -1 : 1;
        }
        return left.label.localeCompare(right.label);
    });
}

function sortActivationEvents(events: string[]): string[] {
    return events.slice().sort((left, right) => {
        const leftPresentation = left.startsWith('Presentation');
        const rightPresentation = right.startsWith('Presentation');
        if (leftPresentation !== rightPresentation) {
            return leftPresentation ? -1 : 1;
        }
        return left.localeCompare(right);
    });
}

const toIsoOrNull = (value: string): string | null => {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const d = new Date(trimmed);
    return Number.isNaN(d.getTime()) ? null : d.toISOString();
};

const toInputDateTime = (value?: string | null): string => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

const DAILY_QUESTS_PRESENTATION_HTML = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1.0" />
  <title>Daily Quests</title>
  <style>
    :root {
      --bg-top: #111827;
      --bg-bottom: #0b1120;
      --card: rgba(18, 26, 45, 0.95);
      --text-main: #f8fafc;
      --text-muted: #94a3b8;
      --gold: #f2c94c;
      --gold-dark: #d7a63a;
      --accent: #38bdf8;
    }
    * { box-sizing: border-box; }
    body {
      margin: 0;
      font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Inter, sans-serif;
      color: var(--text-main);
      background: radial-gradient(circle at 20% 0%, #1f2937 0%, var(--bg-top) 35%, var(--bg-bottom) 100%);
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 20px;
    }
    .card {
      width: min(420px, 100%);
      border-radius: 24px;
      background: linear-gradient(180deg, rgba(30,41,59,.96), var(--card));
      border: 1px solid rgba(148, 163, 184, 0.22);
      box-shadow: 0 24px 70px rgba(0, 0, 0, 0.45);
      overflow: hidden;
    }
    .hero {
      padding: 18px 20px 10px;
      border-bottom: 1px solid rgba(148, 163, 184, 0.15);
    }
    .badge {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      letter-spacing: .06em;
      text-transform: uppercase;
      color: #0f172a;
      background: linear-gradient(180deg, #ffe08a, var(--gold));
      border-radius: 999px;
      padding: 6px 10px;
      font-weight: 700;
    }
    h1 {
      margin: 14px 0 8px;
      font-size: 28px;
      line-height: 1.1;
      letter-spacing: -.02em;
    }
    p {
      margin: 0;
      color: var(--text-muted);
      font-size: 15px;
      line-height: 1.5;
    }
    .quests {
      padding: 16px 20px;
      display: grid;
      gap: 10px;
    }
    .quest {
      border-radius: 14px;
      border: 1px solid rgba(56, 189, 248, 0.22);
      background: rgba(15, 23, 42, 0.75);
      padding: 12px;
    }
    .quest-title {
      font-weight: 700;
      font-size: 14px;
      margin-bottom: 6px;
    }
    .quest-meta {
      display: flex;
      justify-content: space-between;
      align-items: center;
      color: var(--text-muted);
      font-size: 12px;
    }
    .reward {
      color: var(--gold);
      font-weight: 700;
    }
    .actions {
      display: grid;
      gap: 10px;
      padding: 4px 20px 20px;
    }
    button {
      appearance: none;
      border: none;
      border-radius: 12px;
      min-height: 48px;
      font-weight: 700;
      font-size: 15px;
      cursor: pointer;
      transition: transform .12s ease, opacity .2s ease;
    }
    button:active { transform: translateY(1px); }
    .primary {
      background: linear-gradient(180deg, #ffe08a, var(--gold-dark));
      color: #111827;
    }
    .secondary {
      background: rgba(148, 163, 184, 0.12);
      color: var(--text-main);
      border: 1px solid rgba(148, 163, 184, 0.3);
    }
    .tertiary {
      background: transparent;
      color: var(--text-muted);
      border: 1px dashed rgba(148, 163, 184, 0.35);
    }
  </style>
</head>
<body>
  <section class="card">
    <div class="hero">
      <span class="badge">New Feature</span>
      <h1>Daily Quests are live</h1>
      <p>Complete simple missions every day and collect extra betcoins. Track progress and claim rewards from the Daily Quests card.</p>
    </div>

    <div class="quests">
      <div class="quest">
        <div class="quest-title">Place 1 bet today</div>
        <div class="quest-meta"><span>Progress: 0/1</span><span class="reward">+200</span></div>
      </div>
      <div class="quest">
        <div class="quest-title">Open Daily Quests section</div>
        <div class="quest-meta"><span>Progress: 0/1</span><span class="reward">+100</span></div>
      </div>
      <div class="quest">
        <div class="quest-title">Finish all daily quests</div>
        <div class="quest-meta"><span>Progress: 0/3</span><span class="reward">+500</span></div>
      </div>
    </div>

    <div class="actions">
      <button class="primary" onclick="Android.close()">Open Daily Quests</button>
      <button class="secondary" onclick="Android.remindLater()">Remind me later</button>
      <button class="tertiary" onclick="Android.skip()">Skip for now</button>
    </div>
  </section>
</body>
</html>`;

type CampaignFormState = {
    id: string | null;
    slug: string;
    name: string;
    enabled: boolean;
    priority: number;
    htmlContent: string;
    remoteUrl: string;
    screenKeys: string[];
    eventKeys: string[];
    userTags: string[];
    tourCompletion: string;
    osTypes: string[];
    minBuild: number | null;
    maxBuild: number | null;
    maxViewsPerUser: number;
    cooldownMinutes: number;
    startAtInput: string;
    endAtInput: string;
    allowSkip: boolean;
    allowRemindLater: boolean;
    remindLaterMinutesInput: string;
};

const emptyFormState = (): CampaignFormState => ({
    id: null,
    slug: '',
    name: '',
    enabled: false,
    priority: 0,
    htmlContent: '',
    remoteUrl: '',
    screenKeys: [],
    eventKeys: [],
    userTags: [],
    tourCompletion: 'any',
    osTypes: [],
    minBuild: null,
    maxBuild: null,
    maxViewsPerUser: 1,
    cooldownMinutes: 0,
    startAtInput: '',
    endAtInput: '',
    allowSkip: true,
    allowRemindLater: false,
    remindLaterMinutesInput: '',
});

const toFormState = (item: PresentationCampaignDto): CampaignFormState => ({
    id: item.id,
    slug: item.slug,
    name: item.name,
    enabled: item.enabled,
    priority: item.priority,
    htmlContent: item.htmlContent ?? '',
    remoteUrl: item.remoteUrl ?? '',
    screenKeys: [...(item.screenKeys ?? [])],
    eventKeys: [...(item.eventKeys ?? [])],
    userTags: [...(item.userTags ?? [])],
    tourCompletion: item.tourCompletion || 'any',
    osTypes: [...(item.osTypes ?? [])],
    minBuild: item.minBuild ?? null,
    maxBuild: item.maxBuild ?? null,
    maxViewsPerUser: Math.max(1, item.maxViewsPerUser ?? 1),
    cooldownMinutes: Math.max(0, item.cooldownMinutes ?? 0),
    startAtInput: toInputDateTime(item.startAt),
    endAtInput: toInputDateTime(item.endAt),
    allowSkip: item.allowSkip,
    allowRemindLater: item.allowRemindLater,
    remindLaterMinutesInput: item.remindLaterMinutes == null ? '' : String(item.remindLaterMinutes),
});

const toUpsertPayload = (form: CampaignFormState): PresentationCampaignUpsertRequest => {
    const minBuild = form.minBuild;
    const maxBuild = form.maxBuild;
    const remindLaterMinutes = form.allowRemindLater && form.remindLaterMinutesInput.trim().length > 0
        ? Number(form.remindLaterMinutesInput)
        : null;

    return {
        slug: form.slug.trim(),
        name: form.name.trim(),
        enabled: form.enabled,
        priority: Math.trunc(form.priority),
        htmlContent: form.htmlContent.trim() || null,
        remoteUrl: form.remoteUrl.trim() || null,
        templateMeta: {},
        screenKeys: form.screenKeys.map(key => key.trim().toLowerCase()).filter(Boolean),
        eventKeys: form.eventKeys.map(key => key.trim()).filter(Boolean),
        userTags: form.userTags.map(tag => tag.trim()).filter(Boolean),
        tourCompletion: form.tourCompletion,
        osTypes: form.osTypes.map(osType => osType.trim().toLowerCase()).filter(Boolean),
        minBuild,
        maxBuild,
        maxViewsPerUser: Math.max(1, Math.trunc(form.maxViewsPerUser)),
        cooldownMinutes: Math.max(0, Math.trunc(form.cooldownMinutes)),
        startAt: toIsoOrNull(form.startAtInput),
        endAt: toIsoOrNull(form.endAtInput),
        allowSkip: form.allowSkip,
        allowRemindLater: form.allowRemindLater,
        remindLaterMinutes: form.allowRemindLater && Number.isFinite(remindLaterMinutes)
            ? Math.max(1, Math.trunc(remindLaterMinutes as number))
            : null,
    };
};

export default function PresentationCampaignsPage() {
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [saving, setSaving] = React.useState(false);
    const [items, setItems] = React.useState<PresentationCampaignDto[]>([]);
    const [selectedId, setSelectedId] = React.useState<string | null>(null);
    const [form, setForm] = React.useState<CampaignFormState>(emptyFormState);
    const [errors, setErrors] = React.useState<FormErrors>({});
    const [message, setMessage] = React.useState<PageMessage>(null);
    const [htmlDialogOpen, setHtmlDialogOpen] = React.useState(false);
    const [userTagDefinitions, setUserTagDefinitions] = React.useState<UserTagDefinitionDto[]>([]);
    const [userTagsLoading, setUserTagsLoading] = React.useState(true);
    const [screenRegistry, setScreenRegistry] = React.useState<PresentationScreenRegistryDto | null>(null);
    const [screenOptionsLoading, setScreenOptionsLoading] = React.useState(true);
    const [activationEvents, setActivationEvents] = React.useState<string[]>([]);
    const [activationEventsLoading, setActivationEventsLoading] = React.useState(true);
    const [osTypeOptions, setOsTypeOptions] = React.useState<string[]>([]);

    const { versions: appVersions, loading: appVersionsLoading } = useAppVersionsForOsTypes(form.osTypes);

    React.useEffect(() => {
        void (async () => {
            setUserTagsLoading(true);
            setScreenOptionsLoading(true);
            setActivationEventsLoading(true);
            const [tags, registry, funnelOptions] = await Promise.all([
                getUserTags(),
                getPresentationScreenRegistry(),
                getFunnelOptions(),
            ]);
            setUserTagsLoading(false);
            setScreenOptionsLoading(false);
            setActivationEventsLoading(false);
            if (tags) {
                setUserTagDefinitions(tags.slice().sort((left, right) => left.tagName.localeCompare(right.tagName)));
            }
            if (registry) {
                setScreenRegistry(registry);
            }
            if (funnelOptions?.activationEvents) {
                setActivationEvents(sortActivationEvents(funnelOptions.activationEvents));
            }
            if (funnelOptions?.osTypes) {
                setOsTypeOptions(funnelOptions.osTypes);
            }
        })();
    }, []);

    const screenOptions = React.useMemo(
        () => buildPresentationScreenOptions(screenRegistry),
        [screenRegistry],
    );

    const findScreenOption = React.useCallback(
        (screenKey: string) => screenOptions.find(option => option.id === screenKey.trim().toLowerCase()),
        [screenOptions],
    );

    const selectedScreenOptions = React.useMemo(
        () =>
            form.screenKeys.map(screenKey => findScreenOption(screenKey) ?? {
                id: screenKey.trim().toLowerCase(),
                label: screenKey,
                kind: 'screen' as const,
                description: 'Not in current screen registry',
            }),
        [form.screenKeys, findScreenOption],
    );

    const findUserTagDefinition = React.useCallback(
        (tagName: string) =>
            userTagDefinitions.find(
                tag => tag.tagName.localeCompare(tagName, undefined, { sensitivity: 'accent' }) === 0,
            ),
        [userTagDefinitions],
    );

    const selectedUserTagOptions = React.useMemo(
        () =>
            form.userTags.map(tagName => findUserTagDefinition(tagName) ?? {
                id: tagName,
                tagName,
                requirements: [],
                approximateUsersCount: null,
                createdAt: '',
            }),
        [form.userTags, findUserTagDefinition],
    );

    const loadItems = React.useCallback(async () => {
        setLoading(true);
        const response = await getPresentationCampaigns();
        setLoading(false);
        if (response == null) {
            setMessage({ type: 'error', text: 'Failed to load presentation campaigns.' });
            return;
        }
        const sorted = response.slice().sort((left, right) => right.priority - left.priority);
        setItems(sorted);
        if (sorted.length > 0) {
            const selected = selectedId != null
                ? sorted.find(item => item.id === selectedId) ?? sorted[0]
                : sorted[0];
            setSelectedId(selected.id);
            setForm(toFormState(selected));
        } else {
            setSelectedId(null);
            setForm(emptyFormState());
        }
    }, [selectedId]);

    React.useEffect(() => {
        void loadItems();
    }, [loadItems]);

    const validateForm = (current: CampaignFormState): FormErrors => {
        const next: FormErrors = {};
        if (current.slug.trim().length < 2) {
            next.slug = 'Slug must have at least 2 characters.';
        }
        if (current.name.trim().length < 2) {
            next.name = 'Name must have at least 2 characters.';
        }
        if (!current.htmlContent.trim() && !current.remoteUrl.trim()) {
            next.htmlContent = 'Provide either HTML content or remote URL.';
        }
        if (current.enabled && current.screenKeys.length === 0) {
            next.screenKeys = 'Screen trigger is required for active campaigns.';
        }
        if (!Number.isFinite(current.maxViewsPerUser) || current.maxViewsPerUser < 1) {
            next.maxViewsPerUser = 'maxViewsPerUser must be at least 1.';
        }
        if (current.allowRemindLater) {
            const minutes = Number(current.remindLaterMinutesInput);
            if (!current.remindLaterMinutesInput.trim() || !Number.isFinite(minutes) || minutes < 1) {
                next.remindLaterMinutes = 'Remind later minutes must be at least 1.';
            }
        }
        const tourCompletionError = validateTourCompletionFormValue(current.tourCompletion);
        if (tourCompletionError) {
            next.tourCompletion = tourCompletionError;
        }
        return next;
    };

    const saveCampaign = async () => {
        const validationErrors = validateForm(form);
        setErrors(validationErrors);
        if (Object.keys(validationErrors).length > 0) return;

        const payload = toUpsertPayload({
            ...form,
            userTags: form.userTags
                .map(tag => findUserTagDefinition(tag)?.tagName ?? tag.trim())
                .filter(Boolean),
        });
        setSaving(true);
        const result = form.id
            ? await updatePresentationCampaign(form.id, payload)
            : await createPresentationCampaign(payload);
        setSaving(false);
        if (!result.ok || !result.data) {
            setMessage({ type: 'error', text: result.error ?? 'Campaign was not saved.' });
            return;
        }
        setMessage({ type: 'success', text: form.id ? 'Campaign updated.' : 'Campaign created.' });
        await loadItems();
        setSelectedId(result.data.id);
        setForm(toFormState(result.data));
    };

    const deleteCampaign = async () => {
        if (!form.id) return;
        const confirmed = window.confirm('Delete this presentation campaign?');
        if (!confirmed) return;
        setSaving(true);
        const result = await deletePresentationCampaign(form.id);
        setSaving(false);
        if (!result.ok) {
            setMessage({ type: 'error', text: result.error ?? 'Campaign was not deleted.' });
            return;
        }
        setMessage({ type: 'success', text: 'Campaign deleted.' });
        await loadItems();
    };

    const createNewCampaign = () => {
        setSelectedId(null);
        setErrors({});
        setForm(emptyFormState());
    };

    return (
        <Box sx={{ mx: 4, my: 3 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between" gap={2} sx={{ mb: 2 }}>
                <Typography variant="h4">Presentation campaigns</Typography>
                <Stack direction="row" gap={1}>
                    <Button variant="outlined" onClick={() => navigate('/marketing/daily-quests')}>Marketing overview</Button>
                    <Button variant="outlined" onClick={() => navigate('/marketing/presentations/screen-registry')}>
                        Screen registry
                    </Button>
                    <Button variant="contained" onClick={createNewCampaign}>New campaign</Button>
                </Stack>
            </Stack>

            {message && (
                <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
                    {message.text}
                </Alert>
            )}

            <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                    <Card variant="outlined">
                        <CardContent>
                            <Typography variant="h6" sx={{ mb: 1 }}>Campaigns</Typography>
                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                    <CircularProgress size={24} />
                                </Box>
                            ) : items.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">No campaigns yet.</Typography>
                            ) : (
                                <Stack gap={1}>
                                    {items.map(item => (
                                        <Box
                                            key={item.id}
                                            onClick={() => {
                                                setSelectedId(item.id);
                                                setErrors({});
                                                setForm(toFormState(item));
                                            }}
                                            sx={{
                                                p: 1.25,
                                                borderRadius: 1,
                                                border: '1px solid',
                                                borderColor: selectedId === item.id ? 'primary.main' : 'divider',
                                                cursor: 'pointer',
                                                bgcolor: selectedId === item.id ? 'action.selected' : 'transparent',
                                            }}
                                        >
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                                                <Typography variant="subtitle2">{item.name}</Typography>
                                                <Chip size="small" label={item.enabled ? 'active' : 'inactive'} color={item.enabled ? 'success' : 'default'} />
                                            </Stack>
                                            <Typography variant="caption" color="text.secondary" display="block">
                                                slug: {item.slug}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                priority {item.priority} · screens {(item.screenKeys ?? []).length}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={12} md={8}>
                    <Card variant="outlined">
                        <CardContent>
                            <Stack gap={2}>
                                <Typography variant="h6">{form.id ? 'Edit campaign' : 'Create campaign'}</Typography>
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                                    <TextField
                                        label="Slug"
                                        value={form.slug}
                                        onChange={e => setForm(prev => ({ ...prev, slug: e.target.value }))}
                                        error={Boolean(errors.slug)}
                                        helperText={errors.slug}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Name"
                                        value={form.name}
                                        onChange={e => setForm(prev => ({ ...prev, name: e.target.value }))}
                                        error={Boolean(errors.name)}
                                        helperText={errors.name}
                                        fullWidth
                                    />
                                </Stack>

                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                                    <TextField
                                        label="Priority"
                                        type="number"
                                        value={form.priority}
                                        onChange={e => setForm(prev => ({ ...prev, priority: Number(e.target.value) }))}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Max views per user"
                                        type="number"
                                        value={form.maxViewsPerUser}
                                        onChange={e => setForm(prev => ({ ...prev, maxViewsPerUser: Number(e.target.value) }))}
                                        error={Boolean(errors.maxViewsPerUser)}
                                        helperText={errors.maxViewsPerUser}
                                        fullWidth
                                    />
                                    <TextField
                                        label="Cooldown minutes"
                                        type="number"
                                        value={form.cooldownMinutes}
                                        onChange={e => setForm(prev => ({ ...prev, cooldownMinutes: Number(e.target.value) }))}
                                        fullWidth
                                    />
                                </Stack>

                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={form.enabled}
                                            onChange={e => setForm(prev => ({ ...prev, enabled: e.target.checked }))}
                                        />
                                    }
                                    label={form.enabled ? 'Enabled' : 'Disabled'}
                                />

                                <Autocomplete
                                    multiple
                                    options={screenOptions}
                                    value={selectedScreenOptions}
                                    loading={screenOptionsLoading}
                                    disabled={screenOptionsLoading && screenOptions.length === 0}
                                    isOptionEqualToValue={(option, value) => option.id === value.id}
                                    getOptionLabel={option => option.label}
                                    onChange={(_, value) =>
                                        setForm(prev => ({
                                            ...prev,
                                            screenKeys: value.map(option => option.id),
                                        }))
                                    }
                                    fullWidth
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Stack spacing={0.25}>
                                                <Typography variant="body2">
                                                    {option.label}
                                                    {option.kind === 'group' ? ' (group)' : ''}
                                                </Typography>
                                                {option.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {option.description}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Box>
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={option.id}
                                                label={option.label}
                                                size="small"
                                            />
                                        ))
                                    }
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label="Screen keys"
                                            placeholder={screenOptionsLoading ? 'Loading screens...' : 'Select screens or groups'}
                                            error={Boolean(errors.screenKeys)}
                                            helperText={
                                                errors.screenKeys ??
                                                'Required when active. Keys from Screen registry (exact keys or groups).'
                                            }
                                        />
                                    )}
                                />
                                <Autocomplete
                                    multiple
                                    options={activationEvents}
                                    value={form.eventKeys}
                                    loading={activationEventsLoading}
                                    onChange={(_, value) => setForm(prev => ({ ...prev, eventKeys: value }))}
                                    fullWidth
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                                        ))
                                    }
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label="Event keys"
                                            placeholder={activationEventsLoading ? 'Loading events...' : 'Optional — select activation events'}
                                            helperText="Stored for future use; eligibility today uses screen keys only."
                                        />
                                    )}
                                />

                                <Divider />
                                <Typography variant="subtitle2">Targeting</Typography>
                                <Autocomplete
                                    multiple
                                    options={userTagDefinitions}
                                    value={selectedUserTagOptions}
                                    loading={userTagsLoading}
                                    disabled={userTagsLoading && userTagDefinitions.length === 0}
                                    isOptionEqualToValue={(option, value) =>
                                        option.tagName.localeCompare(value.tagName, undefined, { sensitivity: 'accent' }) === 0
                                    }
                                    getOptionLabel={option => option.tagName}
                                    onChange={(_, value) =>
                                        setForm(prev => ({
                                            ...prev,
                                            userTags: value.map(tag => tag.tagName),
                                        }))
                                    }
                                    fullWidth
                                    renderOption={(props, option) => (
                                        <Box component="li" {...props}>
                                            <Stack spacing={0.25}>
                                                <Typography variant="body2">{option.tagName}</Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {option.approximateUsersCount == null
                                                        ? 'Approx. users: counting...'
                                                        : `Approx. users: ${option.approximateUsersCount.toLocaleString()}`}
                                                </Typography>
                                            </Stack>
                                        </Box>
                                    )}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip
                                                {...getTagProps({ index })}
                                                key={option.tagName}
                                                label={option.tagName}
                                                size="small"
                                            />
                                        ))
                                    }
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label="User tags"
                                            placeholder={userTagsLoading ? 'Loading tags...' : 'Optional — select from list'}
                                            helperText={
                                                form.userTags.length === 0
                                                    ? 'No tags selected: campaign is shown to all users (other filters still apply).'
                                                    : 'User needs at least one of the selected tags (OR). Tag names are matched case-insensitively.'
                                            }
                                        />
                                    )}
                                />
                                <Autocomplete
                                    multiple
                                    options={TOUR_COMPLETION_FILTER_OPTIONS.map(option => option.value)}
                                    value={parseTourCompletionFormValue(form.tourCompletion)}
                                    onChange={(_, value) =>
                                        setForm(prev => ({
                                            ...prev,
                                            tourCompletion: serializeTourCompletionFormValue(value),
                                        }))
                                    }
                                    getOptionLabel={value =>
                                        TOUR_COMPLETION_FILTER_OPTIONS.find(option => option.value === value)?.label
                                        ?? value
                                    }
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => {
                                            const label = TOUR_COMPLETION_FILTER_OPTIONS.find(
                                                item => item.value === option,
                                            )?.label ?? option;
                                            return (
                                                <Chip {...getTagProps({ index })} key={option} label={label} size="small" />
                                            );
                                        })
                                    }
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label="Tour status filter"
                                            error={Boolean(errors.tourCompletion)}
                                            helperText={
                                                errors.tourCompletion
                                                ?? 'Leave empty for any user. Select one or more — user must match at least one (OR). Example: finished + skipped together.'
                                            }
                                        />
                                    )}
                                />
                                <Autocomplete
                                    multiple
                                    options={osTypeOptions}
                                    value={form.osTypes}
                                    onChange={(_, value) => setForm(prev => ({ ...prev, osTypes: value }))}
                                    renderTags={(value, getTagProps) =>
                                        value.map((option, index) => (
                                            <Chip {...getTagProps({ index })} key={option} label={option} size="small" />
                                        ))
                                    }
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label="OS types"
                                            helperText="Optional. Leave empty for all platforms. Build list uses selected OS types."
                                        />
                                    )}
                                />
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                                    <BuildNumberAutocomplete
                                        label="Min build"
                                        value={form.minBuild}
                                        onChange={minBuild => setForm(prev => ({ ...prev, minBuild }))}
                                        versions={appVersions}
                                        loading={appVersionsLoading}
                                        peerMax={form.maxBuild}
                                        fullWidth
                                    />
                                    <BuildNumberAutocomplete
                                        label="Max build"
                                        value={form.maxBuild}
                                        onChange={maxBuild => setForm(prev => ({ ...prev, maxBuild }))}
                                        versions={appVersions}
                                        loading={appVersionsLoading}
                                        peerMin={form.minBuild}
                                        fullWidth
                                    />
                                </Stack>
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                                    <TextField
                                        label="Start at"
                                        type="datetime-local"
                                        value={form.startAtInput}
                                        onChange={e => setForm(prev => ({ ...prev, startAtInput: e.target.value }))}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />
                                    <TextField
                                        label="End at"
                                        type="datetime-local"
                                        value={form.endAtInput}
                                        onChange={e => setForm(prev => ({ ...prev, endAtInput: e.target.value }))}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                    />
                                </Stack>

                                <Divider />
                                <Typography variant="subtitle2">Actions</Typography>
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
                                    <FormControlLabel
                                        control={<Switch checked={form.allowSkip} onChange={e => setForm(prev => ({ ...prev, allowSkip: e.target.checked }))} />}
                                        label="Allow skip"
                                    />
                                    <FormControlLabel
                                        control={<Switch checked={form.allowRemindLater} onChange={e => setForm(prev => ({ ...prev, allowRemindLater: e.target.checked }))} />}
                                        label="Allow remind later"
                                    />
                                </Stack>
                                {form.allowRemindLater && (
                                    <TextField
                                        label="Remind later minutes"
                                        type="number"
                                        value={form.remindLaterMinutesInput}
                                        onChange={e => setForm(prev => ({ ...prev, remindLaterMinutesInput: e.target.value }))}
                                        error={Boolean(errors.remindLaterMinutes)}
                                        helperText={errors.remindLaterMinutes}
                                    />
                                )}

                                <Divider />
                                <Typography variant="subtitle2">Content</Typography>
                                <TextField
                                    label="Remote URL (optional)"
                                    value={form.remoteUrl}
                                    onChange={e => setForm(prev => ({ ...prev, remoteUrl: e.target.value }))}
                                    fullWidth
                                />
                                <TextField
                                    label="HTML content"
                                    value={form.htmlContent}
                                    onChange={e => setForm(prev => ({ ...prev, htmlContent: e.target.value }))}
                                    multiline
                                    minRows={10}
                                    maxRows={20}
                                    error={Boolean(errors.htmlContent)}
                                    helperText={errors.htmlContent}
                                    sx={{ '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: 13 } }}
                                />
                                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                                    <Button variant="outlined" onClick={() => setHtmlDialogOpen(true)}>
                                        Open Monaco editor
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        color="secondary"
                                        onClick={() => setForm(prev => ({ ...prev, htmlContent: DAILY_QUESTS_PRESENTATION_HTML }))}
                                    >
                                        Use Daily Quests template
                                    </Button>
                                </Stack>

                                <Box sx={{ pt: 1 }}>
                                    <HtmlTemplatePreview
                                        htmlContent={form.htmlContent}
                                        width={375}
                                        height={667}
                                        borderRadius="20px"
                                        borderWidth="2px"
                                        interactive
                                        showLabel
                                    />
                                </Box>
                            </Stack>
                        </CardContent>
                        <CardActions sx={{ px: 2, pb: 2 }}>
                            <Button variant="contained" onClick={() => void saveCampaign()} disabled={saving}>
                                {saving ? 'Saving...' : (form.id ? 'Update campaign' : 'Create campaign')}
                            </Button>
                            {form.id && (
                                <Button color="error" variant="outlined" onClick={() => void deleteCampaign()} disabled={saving}>
                                    Delete
                                </Button>
                            )}
                        </CardActions>
                    </Card>
                </Grid>
            </Grid>

            <WebGameTemplateHtmlEditorDialog
                open={htmlDialogOpen}
                onClose={() => setHtmlDialogOpen(false)}
                value={form.htmlContent}
                onChange={value => setForm(prev => ({ ...prev, htmlContent: value }))}
                gameCode={form.slug || 'presentation-campaign'}
                templateFileName={form.slug ? `${form.slug}.html` : undefined}
                loading={false}
                saving={false}
                message={null}
                canReset={false}
                onSave={() => setHtmlDialogOpen(false)}
                onReloadFromServer={() => undefined}
            />
        </Box>
    );
}
